import { z } from 'zod';

const MONEY_PATTERN = /^\d+(\.\d{1,4})?$/;

// Preprocess '' / undefined to null so an empty money field reads as "not supplied" rather
// than failing the regex — mirrors the Fees NullableMoney pattern (makeProductFeeSchema).
const NullableMoney = z.preprocess(
    (v) => (v === '' || v === undefined ? null : v),
    // Zero is a legitimate service cost (a free/included service), so the regex accepts 0.00.
    // The message therefore says "0 or greater", not "positive", to match the actual constraint.
    z.union([z.null(), z.string().regex(MONEY_PATTERN, 'Enter a valid amount (0 or greater, up to 4 decimal places)')]),
);

const toWholeNumberOrUndefined = (v: unknown) =>
    v === '' || v === null || v === undefined ? undefined : Number(v);

// Server parity: StorePlanServiceRequest and UpdateServiceRequest cap these at the
// same values, so an over-limit entry is caught here rather than as a 422 round trip.
export const SERVICE_SCHEDULE_LIMITS = {
    position: 25,
    months_from_start: 600,
    mileage_from_start: 1_000_000,
} as const;

const requiredSetPoint = (label: string, max: number, maxLabel: string) => z.preprocess(
    toWholeNumberOrUndefined,
    z.number({ required_error: `${label} is required.`, invalid_type_error: `${label} must be a whole number.` })
        .int(`${label} must be a whole number.`)
        .min(1, `${label} must be 1 or greater.`)
        .max(max, `${label} must be ${maxLabel} or fewer.`),
);

const optionalPosition = z.preprocess(
    toWholeNumberOrUndefined,
    z.number({ invalid_type_error: 'Position must be a whole number.' })
        .int('Position must be a whole number.')
        .min(1, 'Position must be 1 or greater.')
        .max(SERVICE_SCHEDULE_LIMITS.position, `Position must be ${SERVICE_SCHEDULE_LIMITS.position} or fewer.`)
        .optional(),
);

const optionalServiceType = z.string()
    .trim()
    .max(100, 'Service type must be 100 characters or fewer.')
    .optional()
    .or(z.literal(''))
    .transform((v) => (v === '' ? undefined : v));

const inclusionSchema = z.object({
    item_id: z.string().uuid('Select an item.'),
    unit_quantity: z.preprocess(
        toWholeNumberOrUndefined,
        z.number({ required_error: 'Quantity is required.', invalid_type_error: 'Quantity must be a whole number.' })
            .int('Quantity must be a whole number.')
            .min(1, 'Quantity must be 1 or greater.'),
    ),
    unit_price_net: z.string().regex(MONEY_PATTERN, 'Enter a valid amount (0 or greater, up to 4 decimal places)'),
});

export type ServiceInclusionInput = z.input<typeof inclusionSchema>;

export const serviceSchema = z.object({
    name: z.string().min(1, 'Name is required').max(255),
    description: z.string().optional().or(z.literal('')).transform(v => v === '' ? undefined : v),
    status: z.enum(['active', 'retired']).default('active'),
    cost_mode: z.enum(['total', 'breakdown']),
    vat_category_id: z.string().uuid('Select a VAT category'),
    total_cost_net: NullableMoney,
    total_cost_gross: NullableMoney,
    position: optionalPosition,
    service_type: optionalServiceType,
    months_from_start: requiredSetPoint('Months from start', SERVICE_SCHEDULE_LIMITS.months_from_start, '600'),
    mileage_from_start: requiredSetPoint('Mileage from start', SERVICE_SCHEDULE_LIMITS.mileage_from_start, '1,000,000'),
    confirm_inclusions_deletion: z.boolean().optional(),
    items: z.array(inclusionSchema).default([]),
}).superRefine((data, ctx) => {
    if (data.cost_mode === 'total') {
        if (data.total_cost_net == null) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['total_cost_net'], message: 'Enter a net cost.' });
        }
    } else if (data.total_cost_net != null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['total_cost_net'], message: 'Total cost is only for total-mode services.' });
    }

    const seen = new Set<string>();
    data.items.forEach((row, index) => {
        if (seen.has(row.item_id)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['items', index, 'item_id'], message: 'This item is already included.' });
        }
        seen.add(row.item_id);
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
}).transform(({ total_cost_gross: _totalCostGrossOmit, ...rest }) => rest);

export type ServiceFormInput = z.input<typeof serviceSchema>;
export type ServiceFormOutput = z.output<typeof serviceSchema>;

// Submitted shape: `items` is omitted when the inclusion rows were not edited, so a
// stale form cannot overwrite a newer Items-tab write (the API treats it as a no-op).
export type ServiceFormSubmitValues =
    Omit<ServiceFormOutput, 'items'> & { items?: ServiceFormOutput['items'] };
